import { cssRaw, style } from 'typestyle';
import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import StyleConstants from '../../core/styles.constants';
import DashboardTheme from '../statistics/dashboard/dashboard-theme';
import { groupHead, tint } from '../../core/admin-surface.styles';
import type { MessageStatus } from './message-status';

// Communication tab chrome, following the approved communication mockup. Colours come from the
// app's palette (StyleConstants) plus the dashboard tokens; the mockup's green/blue/grey status
// trio maps onto them.
const GREEN = StyleConstants.LIGHT_GREEN_COLOR;
const GREEN_INK = '#3D6B0E';
const BLUE_INK = '#1F6FBC';

export const card: CSSProperties = {
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 14,
  marginBottom: 24,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const sectionHead = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: isMobile ? 'stretch' : 'center',
  justifyContent: 'space-between',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 16,
  padding: 24,
});

export const sectionHeadLeft: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
};

export const tile: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  background: tint(StyleConstants.DARK_BLUE_COLOR, 0.1),
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const sectionTitle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: 0,
};

export const countPill: CSSProperties = {
  background: DashboardTheme.gray100,
  borderRadius: 10,
  padding: '1px 8px',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 11,
  fontWeight: 700,
  color: DashboardTheme.gray700,
};

export const lede: CSSProperties = {
  margin: '2px 0 0',
  fontSize: 13,
  lineHeight: 1.45,
  color: DashboardTheme.gray700,
};

export const sectionBody: CSSProperties = {
  padding: '0 24px 24px',
};

export const bodyDivider: CSSProperties = {
  height: 1,
  background: DashboardTheme.gray200,
  margin: '0 0 24px',
};

// Collapsible buckets ("Finished", "Sent") get a real button so keyboard and screen readers can
// fold them the same way a click does.
export const groupHeadButton: CSSProperties = {
  ...groupHead,
  width: '100%',
  border: 0,
  padding: 0,
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const caret = (collapsed: boolean): CSSProperties => ({
  color: DashboardTheme.gray500,
  lineHeight: 0,
  transform: collapsed ? 'rotate(-90deg)' : undefined,
  transition: 'transform .2s',
});

export const grid = (isMobile: boolean): CSSProperties => ({
  display: 'grid',
  gap: 16,
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
});

// A message's border says at a glance whether it still has work to do: green while it is live or
// queued, red once it is finished or sent.
const LIVE_BORDER = '#76BA1B';
const DONE_BORDER = '#E74C3C';

export const borderFor = (status: MessageStatus): string => (status === 'active' || status === 'scheduled' ? LIVE_BORDER : DONE_BORDER);

export const messageCard = (status: MessageStatus, muted: boolean): NestedCSSProperties => ({
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${borderFor(status)}`,
  borderRadius: 14,
  padding: 20,
  cursor: 'pointer',
  opacity: muted ? 0.72 : 1,
  transitionDuration: '150ms',
  transitionProperty: 'box-shadow, opacity',
  $nest: {
    '&:hover': {
      opacity: 1,
      boxShadow: '0 8px 24px rgba(16, 24, 40, .08)',
    },
  },
});

export const messageTop: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
};

export const editHint: CSSProperties = {
  flex: '0 0 auto',
  color: DashboardTheme.gray300,
  lineHeight: 0,
  marginTop: 4,
};

export const messageGrow: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

// Titles and bodies are free text, so both are clamped to two lines — the full text is one click
// away in the edit modal. The clamp lives in raw CSS because typestyle's types don't cover
// -webkit-box-orient, without which -webkit-line-clamp does nothing.
export const clampToTwoLines = style({
  display: '-webkit-box',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
});

cssRaw(
  `
  .${clampToTwoLines} {
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  `,
);

export const messageTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: '10px 0 0',
};

export const messageBody: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: DashboardTheme.gray700,
  margin: '4px 0 0',
};

export const messageWhen: CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  color: DashboardTheme.gray500,
  marginTop: 12,
};

const STATUS_COLOURS: Record<MessageStatus, { background: string; color: string; dot: string; border?: string }> = {
  active: { background: tint(GREEN, 0.16), color: GREEN_INK, dot: GREEN },
  scheduled: { background: tint(StyleConstants.MEDIUM_BLUE_COLOR, 0.14), color: BLUE_INK, dot: StyleConstants.MEDIUM_BLUE_COLOR },
  sent: {
    background: StyleConstants.TRANSPARENT_COLOR,
    color: DashboardTheme.gray500,
    dot: DashboardTheme.gray300,
    border: DashboardTheme.gray300,
  },
  past: {
    background: StyleConstants.TRANSPARENT_COLOR,
    color: DashboardTheme.gray500,
    dot: DashboardTheme.gray300,
    border: DashboardTheme.gray300,
  },
};

export const statusChip = (status: MessageStatus): CSSProperties => {
  const colours = STATUS_COLOURS[status];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 24,
    padding: '0 10px 0 8px',
    borderRadius: 12,
    border: colours.border ? `1px solid ${colours.border}` : undefined,
    background: colours.background,
    color: colours.color,
    fontSize: 11.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
};

export const statusDot = (status: MessageStatus): CSSProperties => ({
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: STATUS_COLOURS[status].dot,
});

// Extra marker for announcements on a non-working day / recurring notifications.
export const tag: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  marginLeft: 6,
  height: 24,
  padding: '0 10px',
  borderRadius: 12,
  background: tint(StyleConstants.ORANGE_COLOR, 0.22),
  color: '#8A5A06',
  fontSize: 11.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

export const progressBar: CSSProperties = {
  height: 4,
  borderRadius: 2,
  background: DashboardTheme.gray100,
  marginTop: 16,
  overflow: 'hidden',
};

export const progressFill = (percentage: number): CSSProperties => ({
  display: 'block',
  height: '100%',
  borderRadius: 2,
  width: `${percentage}%`,
  background: GREEN,
});

export const emptyMessage: CSSProperties = {
  display: 'block',
  color: DashboardTheme.gray500,
  fontSize: 13.5,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const errorMessage: CSSProperties = {
  ...emptyMessage,
  color: DashboardTheme.red500,
  fontWeight: 600,
};
